import { XMarkIcon } from '@heroicons/react/24/solid';
import { Button, Switch } from '@heroui/react';
import { ErrorMessage } from '@hookform/error-message';
import type { FC } from 'react';
import { Controller, FormProvider, type SubmitHandler, useForm } from 'react-hook-form';
import { FrequencySelect } from '@/app/components/frequency-select.tsx';
import type { AccountCategoryModel } from '@/app/types/account-category.type.ts';
import { Dialog, DialogActions, DialogBody } from '@/primitives/dialog.tsx';
import { Description, Field, FieldErrorMessage, Fieldset, Label } from '@/primitives/fieldset.tsx';
import { Input } from '@/primitives/input.tsx';

export interface AccountCategoryFormDialogProps {
  action: AccountCategoryActionEnum;
  initialValue: Partial<AccountCategoryModel>;
  onClose: (value: AccountCategoryModel) => void;
  isOpen: boolean;
  setIsOpen: (value: boolean) => void;
}

export enum AccountCategoryActionEnum {
  add = 1,
  edit = 2,
}

export const AccountCategoryFormDialog: FC<Partial<AccountCategoryFormDialogProps>> = ({
  initialValue,
  onClose,
  setIsOpen,
  isOpen,
  action,
}) => {
  const form = useForm<Partial<AccountCategoryModel>>({
    values: initialValue,
  });

  const { register, control, handleSubmit, formState } = form;

  const onSubmit: SubmitHandler<AccountCategoryModel> = value => {
    if (onClose) {
      onClose(value);
    }
    form.reset();
  };

  return (
    <Dialog onClose={setIsOpen} open={isOpen}>
      <FormProvider {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className='flex flex-row justify-between'>
            <h6 className='text-primary-600'>
              {action === AccountCategoryActionEnum.add ? 'Add' : 'Edit'} Account Category
            </h6>
            <Button
              size='sm'
              isIconOnly
              variant='light'
              onClick={() => {
                form.reset();
                setIsOpen(false);
              }}
            >
              <XMarkIcon />
            </Button>
          </div>

          <DialogBody>
            <Fieldset>
              <div className='rounded-xl border border-default-200 bg-default-50 px-4 py-3'>
                <div className='flex items-start gap-3'>
                  <Controller
                    control={control}
                    name='isDefault'
                    render={({ field: { onChange, value } }) => (
                      <Switch
                        id='account-category-is-default'
                        color='primary'
                        classNames={{ base: 'mt-0.5 shrink-0' }}
                        isSelected={Boolean(value)}
                        onValueChange={onChange}
                      />
                    )}
                  />
                  <div className='min-w-0'>
                    <Label
                      htmlFor='account-category-is-default'
                      className='cursor-pointer font-semibold text-default-800'
                    >
                      Default category
                    </Label>
                    <Description className='mt-0.5 text-default-500'>
                      Mark as the default category for this account. At least one category must stay
                      default.
                    </Description>
                  </div>
                </div>
              </div>
              <Field className='mt-4'>
                <Label required={true} className='!mb-0'>
                  Description
                </Label>
                <Input
                  className='!mt-0'
                  placeholder='Category Name'
                  type='text'
                  invalid={!!formState.errors.description}
                  {...register('description', {
                    required: 'This field is required',
                  })}
                />
                <ErrorMessage
                  name={'description'}
                  as={<FieldErrorMessage />}
                  errors={form.formState.errors}
                />
              </Field>
              <FrequencySelect name='frequency' />
            </Fieldset>
          </DialogBody>
          <DialogActions>
            <Button type='submit' color='primary' isDisabled={!formState.isValid}>
              Save
            </Button>
          </DialogActions>
        </form>
      </FormProvider>
    </Dialog>
  );
};
